import { useEffect, useMemo, useState } from 'react'
import Modal from '@components/shared/Modal'
import { USER_PROFILE_OPTIONS, USER_STATUS_OPTIONS } from '@services/userManagementService'

const INITIAL_FORM = {
  fullName: '',
  email: '',
  profile: 'LAWYER',
  status: 'ACTIVE',
  phone: '',
  roleTitle: '',
  password: '',
  confirmPassword: '',
  inviteByEmail: true,
}

function validateForm(values, mode) {
  const errors = {}

  if (!values.fullName.trim()) errors.fullName = 'Informe o nome completo.'
  if (!values.email.trim()) errors.email = 'Informe o email.'
  else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(values.email)) errors.email = 'Informe um email válido.'
  if (!values.profile) errors.profile = 'Selecione o perfil.'
  if (!values.status) errors.status = 'Selecione o status.'

  if (mode === 'create' || values.password || values.confirmPassword) {
    if ((values.password || '').length < 8) errors.password = 'A senha deve ter no mínimo 8 caracteres.'
    if (values.confirmPassword !== values.password) errors.confirmPassword = 'A confirmação da senha deve ser idêntica.'
  }

  return errors
}

export default function UserFormDrawer({ isOpen, mode, user, saving, onClose, onSubmit, saveError, roles = [] }) {
  const [formValues, setFormValues] = useState(INITIAL_FORM)
  const [errors, setErrors] = useState({})

  useEffect(() => {
    if (!isOpen) return
    setErrors({})
    setFormValues(
      user
        ? {
            fullName: user.fullName || '',
            email: user.email || '',
            profile: user.roleId ? String(user.roleId) : (user.profile || 'LAWYER'),
            status: user.status || 'ACTIVE',
            phone: user.phone || '',
            roleTitle: user.roleTitle || '',
            password: '',
            confirmPassword: '',
            inviteByEmail: Boolean(user.inviteByEmail),
          }
        : INITIAL_FORM,
    )
  }, [isOpen, user])

  const title = useMemo(() => (mode === 'create' ? 'Novo usuário' : 'Editar usuário'), [mode])

  const handleChange = (field, value) => {
    setFormValues((prev) => ({ ...prev, [field]: value }))
  }

  const handleSubmit = (event) => {
    event.preventDefault()
    const validation = validateForm(formValues, mode)
    setErrors(validation)
    if (Object.keys(validation).length > 0) return
    onSubmit(formValues)
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={title}
      subtitle="Preencha os dados para gerenciar acessos, convites e permissões do usuário."
      icon="fa-user-plus"
      size="large"
      footer={
        <>
          <button type="button" className="btn btn-outline" onClick={onClose}>Cancelar</button>
          <button type="submit" form="user-form" className="btn btn-primary users-submit-btn" disabled={saving}>
            {saving ? 'Salvando...' : mode === 'create' ? 'Criar usuário' : 'Salvar alterações'}
          </button>
        </>
      }
    >
      <form id="user-form" className="users-form-grid" onSubmit={handleSubmit}>
        {saveError ? <div className="users-inline-error">{saveError}</div> : null}
        <div className="users-form-field users-form-field-full">
          <label>Nome completo</label>
          <input value={formValues.fullName} onChange={(e) => handleChange('fullName', e.target.value)} className="users-text-input" />
          {errors.fullName ? <span className="users-field-error">{errors.fullName}</span> : null}
        </div>
        <div className="users-form-field">
          <label>Email</label>
          <input type="email" value={formValues.email} onChange={(e) => handleChange('email', e.target.value)} className="users-text-input" />
          {errors.email ? <span className="users-field-error">{errors.email}</span> : null}
        </div>
        <div className="users-form-field">
          <label>Telefone</label>
          <input value={formValues.phone} onChange={(e) => handleChange('phone', e.target.value)} className="users-text-input" placeholder="Opcional" />
        </div>
        <div className="users-form-field">
          <label>Perfil</label>
          <select value={formValues.profile} onChange={(e) => handleChange('profile', e.target.value)} className="users-select">
            {(roles.length ? roles.filter((role) => ![3, 4].includes(role.id)).map((role) => ({ value: String(role.id), label: role.nome === 'ADMIN' ? 'Administrador' : role.nome === 'LAWYER' ? 'Advogado' : role.nome })) : USER_PROFILE_OPTIONS).map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
          </select>
          {errors.profile ? <span className="users-field-error">{errors.profile}</span> : null}
        </div>
        <div className="users-form-field">
          <label>Status</label>
          <select value={formValues.status} onChange={(e) => handleChange('status', e.target.value)} className="users-select">
            {USER_STATUS_OPTIONS.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
          </select>
          {errors.status ? <span className="users-field-error">{errors.status}</span> : null}
        </div>
        <div className="users-form-field users-form-field-full">
          <label>Cargo</label>
          <input value={formValues.roleTitle} onChange={(e) => handleChange('roleTitle', e.target.value)} className="users-text-input" placeholder="Opcional" />
        </div>
        <div className="users-form-field">
          <label>{mode === 'create' ? 'Senha' : 'Nova senha'}</label>
          <input type="password" value={formValues.password} onChange={(e) => handleChange('password', e.target.value)} className="users-text-input" placeholder={mode === 'create' ? 'Mínimo de 8 caracteres' : 'Preencha apenas se quiser redefinir'} />
          {errors.password ? <span className="users-field-error">{errors.password}</span> : null}
        </div>
        <div className="users-form-field">
          <label>Confirmar senha</label>
          <input type="password" value={formValues.confirmPassword} onChange={(e) => handleChange('confirmPassword', e.target.value)} className="users-text-input" />
          {errors.confirmPassword ? <span className="users-field-error">{errors.confirmPassword}</span> : null}
        </div>
        <label className="users-checkbox-row users-form-field-full">
          <input type="checkbox" checked={formValues.inviteByEmail} onChange={(e) => handleChange('inviteByEmail', e.target.checked)} />
          <span>Enviar convite por email após salvar o usuário</span>
        </label>
      </form>
    </Modal>
  )
}
